import { AuthenticationResult, PublicClientApplication } from '@azure/msal-browser';
import environment from 'environment';

export class Auth {
  public isAuthenticated: boolean;
  private clientApplication: PublicClientApplication
  private userName: string;

  constructor() {
    this.isAuthenticated = false;
    this.clientApplication = new PublicClientApplication({
      auth: {
        clientId: environment.authSettings.clientId,
        authority: environment.authSettings.authority
      },
      cache: {
        cacheLocation: "localStorage", // This configures where your cache will be stored - use localStorage instead of sessionStorage to support more browser windows
        storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
      }
    });
  }

  public async initialize(): Promise<void> {
    await this.clientApplication.initialize();
  }

  public async ensureIsAuthenticated(): Promise<void> {
    if (this.isAuthenticated)
      return;
    else {
      const refreshAuthenticationResult = await this.refreshAuthentication();
      if (refreshAuthenticationResult !== null && refreshAuthenticationResult.accessToken !== null) {
        this.isAuthenticated = true;
        this.userName = refreshAuthenticationResult.account.username;
        return;
      }
      await this.login();
    }
  }

  public handleRedirectReponse(): Promise<void> {
    return this.clientApplication
      .handleRedirectPromise()
      .then((response) => this.handleResponseResult(response));
  }

  public async getApiTokenSilently(): Promise<AuthenticationResult> {
    try {
      return await this.getApiTokenSilentlyRequest();
    }
    catch (error) {
      console.log(error);
      await this.login();
      return this.getApiTokenSilentlyRequest();
    }
  }

  public async logout(): Promise<void> {
    await this.clientApplication.logout({
      account: this.clientApplication.getAccountByUsername(this.userName)
    });
    this.isAuthenticated = false;
    this.userName = null;
  }

  private getApiTokenSilentlyRequest(): Promise<AuthenticationResult> {
    return this.clientApplication.acquireTokenSilent({
      scopes: this.getApiScopes(),
      account: this.clientApplication.getAccountByUsername(this.userName),
      forceRefresh: false
    });
  }

  private async refreshAuthentication(): Promise<AuthenticationResult> {
    const accounts = this.clientApplication.getAllAccounts();
    if (accounts != null && accounts.length === 1 && accounts[0].username != null && accounts[0].username.length > 0) {
      try {
        return await this.clientApplication.acquireTokenSilent({
          scopes: this.getApiScopes(),
          account: accounts[0],
          forceRefresh: true
        })
      }
      catch (error) {
        console.log(error);
      }
    }
    return null;
  }

  private login(): Promise<void> {
    return this.clientApplication.acquireTokenRedirect({
      scopes: this.getApiScopes(),
      redirectUri: environment.authSettings.redirectUri
    });
  }

  private handleResponseResult(response: AuthenticationResult) {
    if (response != null) {
      if (response.account.username != null && response.account.username.length > 0) {
        this.isAuthenticated = true;
        this.userName = response.account.username;
      }
      else {
        throw new Error("AuthenticationResult is not valid");
      }
    }
  }

  private getApiScopes(): string[] {
    return [`api://${environment.authSettings.apiClientId}/access_as_user`];
  }
}
